/* eslint-disable react-hooks/exhaustive-deps */
import React, { useEffect, useState } from "react";
import { useNavigate, Link, useParams } from "react-router-dom";
import { toast } from "sonner";
import {
  Truck, Package, CheckCircle, XCircle, MapPin, Phone, ArrowRight, ArrowLeft,
  SignOut, ShieldCheck, Clock, GpsFix, Warning, User, House, Receipt,
} from "@phosphor-icons/react";
import {
  courierApi, fmtCourierErr, setCourierToken, clearCourierToken,
  isCourierAuthed,
} from "../../lib/courierApi";

// ============================================================
// Magic-link landing — /delivery-app/auth (token in URL hash)
// ============================================================
export function CourierAuthExchange() {
  const nav = useNavigate();
  const [state, setState] = useState({ phase: "exchanging" });

  useEffect(() => {
    const hash = window.location.hash || "";
    const m = hash.match(/token=([^&]+)/);
    if (!m) {
      setState({ phase: "error",
                  msg: "This page expects a login token in the URL. Ask your partner to send you a fresh link." });
      return;
    }
    courierApi.post("/delivery-app/auth", { token: decodeURIComponent(m[1]) })
      .then(r => {
        setCourierToken(r.data.access_token);
        // Clear the token from the URL so it's not in browser history
        window.history.replaceState(null, "", "/delivery-app");
        nav("/delivery-app", { replace: true });
      })
      .catch(e => setState({ phase: "error", msg: fmtCourierErr(e) }));
  }, []);

  return (
    <div className="min-h-screen grid place-items-center bg-[#0B1220] text-white p-6">
      <div className="max-w-sm w-full text-center space-y-4" data-testid="courier-auth">
        {state.phase === "exchanging" ? (
          <>
            <div className="w-12 h-12 mx-auto rounded-full border-2 border-[#2D5A27] border-t-transparent animate-spin"/>
            <div className="font-heading text-2xl">Signing you in…</div>
            <div className="text-sm text-white/60">One moment while we set up your account.</div>
          </>
        ) : (
          <>
            <div className="w-12 h-12 mx-auto rounded-full bg-rose-500/20 grid place-items-center text-rose-300">
              <Warning size={20} weight="fill"/>
            </div>
            <div className="font-heading text-2xl">Couldn't sign you in</div>
            <div className="text-sm text-white/70">{state.msg}</div>
            <a href="tel:" className="inline-block text-xs underline text-white/80">
              Call your partner manager for a new link.
            </a>
          </>
        )}
      </div>
    </div>
  );
}

// ============================================================
// Fallback / empty entry page  — /delivery-app/login
// ============================================================
export function CourierLogin() {
  const nav = useNavigate();
  useEffect(() => {
    if (isCourierAuthed()) nav("/delivery-app", { replace: true });
  }, []);
  return (
    <div className="min-h-screen bg-[#0B1220] text-white p-6 flex flex-col">
      <div className="flex-1 grid place-items-center">
        <div className="max-w-sm text-center space-y-5" data-testid="courier-login">
          <div className="w-16 h-16 mx-auto rounded-2xl grid place-items-center bg-[#2D5A27]">
            <Truck size={28} weight="duotone"/>
          </div>
          <div>
            <div className="text-[10px] uppercase tracking-[0.22em] text-white/60">
              Rescigo Courier
            </div>
            <h1 className="font-heading text-3xl mt-1">Tap the link your partner sent.</h1>
          </div>
          <p className="text-sm text-white/70 leading-relaxed">
            Your partner will share a personal login link via WhatsApp or SMS.
            Tap the link on your phone — that's it. We don't need a password.
          </p>
          <div className="bg-white/5 rounded-xl p-4 text-xs text-white/70">
            Already received a link?<br/>
            Open it from your <b>messages app</b>, not from this page.
          </div>
        </div>
      </div>
      <footer className="text-center text-[10px] text-white/40">Rescigo · Last-mile portal</footer>
    </div>
  );
}

// ============================================================
// Home — /delivery-app  (active task list)
// ============================================================
const TASK_STATUS = {
  assigned:         { label: "Assigned",         color: "#0B2B66", Icon: User },
  picked_up:        { label: "Picked up",         color: "#C9A227", Icon: Package },
  out_for_delivery: { label: "On the way",        color: "#C25D38", Icon: Truck },
  delivered:        { label: "Delivered",         color: "#1A3317", Icon: CheckCircle },
  failed:           { label: "Failed",             color: "#7A2E18", Icon: XCircle },
};

export function CourierHome() {
  const nav = useNavigate();
  const [me, setMe] = useState(null);
  const [tasks, setTasks] = useState([]);
  const [loading, setLoading] = useState(true);

  const load = async () => {
    try {
      const [m, t] = await Promise.all([
        courierApi.get("/delivery-app/me"),
        courierApi.get("/delivery-app/tasks"),
      ]);
      setMe(m.data);
      setTasks(t.data.tasks || []);
    } catch (e) {
      if (e?.response?.status === 401 || e?.response?.status === 403) {
        clearCourierToken();
        nav("/delivery-app/login", { replace: true });
      } else toast.error(fmtCourierErr(e));
    }
    setLoading(false);
  };
  useEffect(() => {
    if (!isCourierAuthed()) { nav("/delivery-app/login", { replace: true }); return; }
    load();
  }, []);

  if (loading) return <Splash msg="Loading your deliveries…"/>;

  return (
    <div className="min-h-screen bg-[#F7F6F2]" data-testid="courier-home">
      <header className="bg-[#0B1220] text-white sticky top-0 z-20">
        <div className="max-w-md mx-auto px-5 py-3 flex items-center gap-3">
          <div className="w-9 h-9 rounded-lg bg-[#2D5A27] grid place-items-center">
            <Truck size={15} weight="duotone"/>
          </div>
          <div className="flex-1 min-w-0">
            <div className="text-[9px] uppercase tracking-[0.22em] text-white/60">
              {me?.partner?.name}
            </div>
            <div className="font-heading text-base truncate">Hello, {me?.staff?.name?.split(" ")[0]}</div>
          </div>
          <button onClick={() => { clearCourierToken(); nav("/delivery-app/login"); }}
                   className="p-2 text-white/60 hover:text-white">
            <SignOut size={13}/>
          </button>
        </div>
      </header>

      <main className="max-w-md mx-auto px-5 py-6 space-y-4">
        <div>
          <div className="text-[10px] uppercase tracking-[0.22em] text-[#5C5A55]">Active deliveries</div>
          <div className="font-heading text-2xl text-[#1A1A1A]">{tasks.length} {tasks.length === 1 ? "task" : "tasks"}</div>
        </div>

        {tasks.length === 0 ? (
          <div className="bg-white rounded-xl border border-[#E5E2D9] p-8 text-center">
            <CheckCircle size={32} weight="duotone" className="mx-auto text-[#2D5A27] mb-2"/>
            <div className="font-heading text-lg">You're all caught up</div>
            <div className="text-xs text-[#5C5A55] mt-1">New deliveries will appear here automatically.</div>
          </div>
        ) : (
          <div className="space-y-3">
            {tasks.map(t => {
              const m = TASK_STATUS[t.status] || TASK_STATUS.assigned;
              return (
                <Link key={t.id} to={`/delivery-app/tasks/${t.id}`}
                       data-testid={`courier-task-${t.id}`}
                       className="block bg-white rounded-xl border border-[#E5E2D9] p-4 hover:border-[#2D5A27] active:bg-[#F0F7EE] transition">
                  <div className="flex items-start gap-3">
                    <div className="w-10 h-10 rounded-md grid place-items-center flex-none"
                          style={{ background: m.color + "20", color: m.color }}>
                      <m.Icon size={14} weight="duotone"/>
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2">
                        <span className="font-mono text-[10px] text-[#8A8780]">{t.order_no}</span>
                        <span className="text-[9px] uppercase tracking-[0.18em] px-1.5 py-0.5 rounded"
                               style={{ background: m.color + "20", color: m.color }}>
                          {m.label}
                        </span>
                      </div>
                      <div className="font-heading text-base text-[#1A1A1A] mt-0.5 truncate">{t.customer_name}</div>
                      <div className="text-[11px] text-[#5C5A55] line-clamp-2">{t.delivery_address}</div>
                    </div>
                    {t.amount_to_collect > 0 && (
                      <span className="px-2 py-0.5 bg-[#FBE7E0] text-[#7A2E18] text-[10px] font-mono rounded">
                        ₹{t.amount_to_collect.toFixed(0)}
                      </span>
                    )}
                  </div>
                </Link>
              );
            })}
          </div>
        )}
      </main>
    </div>
  );
}

// ============================================================
// Task detail — /delivery-app/tasks/:id
// ============================================================
export function CourierTaskDetail() {
  const { id } = useParams();
  const nav = useNavigate();
  const [task, setTask] = useState(null);
  const [otp, setOtp] = useState("");
  const [busy, setBusy] = useState(false);
  const [showOtp, setShowOtp] = useState(false);

  const load = async () => {
    try {
      const r = await courierApi.get(`/delivery-app/tasks/${id}`);
      setTask(r.data);
    } catch (e) {
      if (e?.response?.status === 401) {
        clearCourierToken();
        nav("/delivery-app/login", { replace: true });
      } else toast.error(fmtCourierErr(e));
    }
  };
  useEffect(() => {
    if (!isCourierAuthed()) { nav("/delivery-app/login", { replace: true }); return; }
    load();
  }, [id]);

  const grabGps = () => new Promise((res) => {
    if (!navigator.geolocation) return res({});
    navigator.geolocation.getCurrentPosition(
      (p) => res({ lat: p.coords.latitude, lng: p.coords.longitude }),
      () => res({}),
      { timeout: 5000, enableHighAccuracy: true },
    );
  });

  const action = async (path, label) => {
    setBusy(true);
    try {
      const coords = await grabGps();
      await courierApi.post(`/delivery-app/tasks/${id}/${path}`, coords);
      toast.success(label);
      await load();
    } catch (e) { toast.error(fmtCourierErr(e)); }
    setBusy(false);
  };

  const complete = async () => {
    if (otp.length !== 6) { toast.error("Enter the 6-digit OTP the customer shows you"); return; }
    setBusy(true);
    try {
      const coords = await grabGps();
      await courierApi.post(`/delivery-app/tasks/${id}/complete`,
                              { otp, ...coords });
      toast.success("Delivery verified · marked delivered");
      setShowOtp(false);
      setOtp("");
      nav("/delivery-app", { replace: true });
    } catch (e) { toast.error(fmtCourierErr(e)); }
    setBusy(false);
  };

  const fail = async () => {
    const reason = window.prompt("Reason this delivery failed?");
    if (!reason) return;
    setBusy(true);
    try {
      await courierApi.post(`/delivery-app/tasks/${id}/fail`, { reason });
      toast.success("Marked failed");
      nav("/delivery-app", { replace: true });
    } catch (e) { toast.error(fmtCourierErr(e)); }
    setBusy(false);
  };

  if (!task) return <Splash msg="Loading task…"/>;
  const m = TASK_STATUS[task.status] || TASK_STATUS.assigned;

  return (
    <div className="min-h-screen bg-[#F7F6F2] pb-32" data-testid="courier-task-detail">
      <header className="bg-[#0B1220] text-white sticky top-0 z-20">
        <div className="max-w-md mx-auto px-5 py-3 flex items-center gap-3">
          <Link to="/delivery-app" className="text-white/70 hover:text-white">
            <ArrowLeft size={16}/>
          </Link>
          <div className="flex-1 min-w-0">
            <div className="font-mono text-[10px] text-white/60">{task.order_no}</div>
            <div className="font-heading text-base truncate">{task.customer_name}</div>
          </div>
          <span className="text-[10px] uppercase tracking-[0.18em] px-2 py-0.5 rounded"
                style={{ background: m.color + "30", color: "white" }}>
            {m.label}
          </span>
        </div>
      </header>

      <main className="max-w-md mx-auto px-5 py-5 space-y-4">
        {/* Customer + address card */}
        <section className="bg-white rounded-xl border border-[#E5E2D9] p-4 space-y-3">
          <div className="flex items-start gap-2">
            <MapPin size={16} weight="duotone" className="text-[#2D5A27] mt-0.5 flex-none"/>
            <div className="flex-1">
              <div className="text-[10px] uppercase tracking-[0.22em] text-[#5C5A55]">Deliver to</div>
              <div className="text-sm text-[#1A1A1A] mt-0.5">{task.delivery_address}</div>
            </div>
          </div>
          <div className="grid grid-cols-2 gap-2">
            {task.customer_phone && (
              <a href={`tel:${task.customer_phone}`}
                  data-testid="call-customer"
                  className="h-11 rounded-lg border border-[#E5E2D9] bg-white inline-flex items-center justify-center gap-1.5 text-xs font-medium text-[#1A1A1A]">
                <Phone size={12} weight="bold"/> Call customer
              </a>
            )}
            <a href={`https://www.google.com/maps/dir/?api=1&destination=${encodeURIComponent(task.delivery_address || "")}`}
                target="_blank" rel="noreferrer"
                className="h-11 rounded-lg border border-[#E5E2D9] bg-white inline-flex items-center justify-center gap-1.5 text-xs font-medium text-[#1A1A1A]">
              <MapPin size={12} weight="bold"/> Open maps
            </a>
          </div>
        </section>

        {/* Items */}
        <section className="bg-white rounded-xl border border-[#E5E2D9] p-4">
          <div className="text-[10px] uppercase tracking-[0.22em] text-[#5C5A55] mb-2">Items</div>
          <ul className="text-sm space-y-1.5">
            {(task.items_summary || []).map((it, i) => (
              <li key={i} className="flex justify-between">
                <span className="text-[#1A1A1A]">{it.name}</span>
                <span className="text-[#5C5A55] font-mono text-xs">× {it.qty}</span>
              </li>
            ))}
          </ul>
          {task.has_rental && (
            <div className="mt-2 px-2 py-1 bg-[#FBE7E0] text-[#7A2E18] text-[10px] uppercase tracking-[0.18em] rounded inline-block">
              Includes rental — handover signed slip
            </div>
          )}
        </section>

        {/* COD callout */}
        {task.amount_to_collect > 0 && (
          <section className="bg-[#FBE7E0] border border-[#F4C7B9] rounded-xl p-4 flex items-start gap-3">
            <Warning size={18} weight="fill" className="text-[#7A2E18] flex-none mt-0.5"/>
            <div>
              <div className="text-[10px] uppercase tracking-[0.22em] text-[#7A2E18] font-semibold">
                Cash on delivery
              </div>
              <div className="font-heading text-2xl text-[#7A2E18]">
                Collect ₹{task.amount_to_collect.toFixed(2)}
              </div>
            </div>
          </section>
        )}
      </main>

      {/* Sticky action bar */}
      <div className="fixed bottom-0 inset-x-0 bg-white border-t border-[#E5E2D9] pb-[env(safe-area-inset-bottom)]">
        <div className="max-w-md mx-auto px-5 py-3 space-y-2">
          {task.status === "assigned" && (
            <>
              <button onClick={() => action("picked-up", "Picked up from store")}
                       disabled={busy} data-testid="btn-pickup"
                       className="w-full h-12 rounded-lg bg-[#C9A227] text-white font-medium text-sm inline-flex items-center justify-center gap-2">
                <Package size={14} weight="bold"/> Mark picked up
              </button>
              <button onClick={() => action("out-for-delivery", "Out for delivery")}
                       disabled={busy} data-testid="btn-dispatch"
                       className="w-full h-12 rounded-lg bg-[#C25D38] text-white font-medium text-sm inline-flex items-center justify-center gap-2">
                <Truck size={14} weight="bold"/> Start delivery (captures GPS)
              </button>
            </>
          )}
          {task.status === "picked_up" && (
            <button onClick={() => action("out-for-delivery", "Out for delivery")}
                     disabled={busy} data-testid="btn-dispatch"
                     className="w-full h-12 rounded-lg bg-[#C25D38] text-white font-medium text-sm inline-flex items-center justify-center gap-2">
              <Truck size={14} weight="bold"/> Start delivery
            </button>
          )}
          {task.status === "out_for_delivery" && (
            <>
              <button onClick={() => setShowOtp(true)} disabled={busy}
                       data-testid="btn-complete"
                       className="w-full h-12 rounded-lg bg-[#2D5A27] text-white font-medium text-sm inline-flex items-center justify-center gap-2">
                <ShieldCheck size={14} weight="fill"/> I'm at the door — verify OTP
              </button>
              <button onClick={fail} disabled={busy}
                       className="w-full h-10 rounded-lg border border-[#F4C7B9] text-[#7A2E18] text-xs">
                Mark as failed
              </button>
            </>
          )}
          {(task.status === "delivered" || task.status === "failed") && (
            <Link to="/delivery-app"
                  className="block text-center h-12 leading-[3rem] rounded-lg bg-[#0B1220] text-white text-sm font-medium">
              ← Back to tasks
            </Link>
          )}
        </div>
      </div>

      {/* OTP entry modal */}
      {showOtp && (
        <div className="fixed inset-0 z-30 bg-black/60 grid place-items-end p-3"
              onClick={() => setShowOtp(false)} data-testid="otp-modal">
          <div className="bg-white rounded-t-2xl rounded-b-2xl w-full p-6 space-y-4"
                onClick={e => e.stopPropagation()}>
            <div>
              <div className="text-[10px] uppercase tracking-[0.22em] text-[#5C5A55]">Proof of delivery</div>
              <div className="font-heading text-xl text-[#1A1A1A]">Ask customer for the OTP</div>
              <div className="text-xs text-[#5C5A55] mt-1">
                They'll see a 6-digit code on their order screen.
              </div>
            </div>
            <input value={otp} maxLength={6} inputMode="numeric" autoFocus
                    onChange={e => setOtp(e.target.value.replace(/\D/g, ""))}
                    data-testid="otp-input"
                    placeholder="••••••"
                    className="w-full h-16 px-3 border border-[#E5E2D9] rounded-lg text-4xl font-mono tracking-[0.4em] text-center outline-none focus:border-[#2D5A27]"/>
            <button onClick={complete} disabled={busy || otp.length !== 6}
                     data-testid="otp-submit"
                     className="w-full h-12 rounded-lg bg-[#2D5A27] text-white font-medium text-sm disabled:opacity-60">
              {busy ? "Verifying…" : "Verify & mark delivered"}
            </button>
            <button onClick={() => setShowOtp(false)}
                     className="w-full h-10 text-xs text-[#5C5A55]">Cancel</button>
          </div>
        </div>
      )}
    </div>
  );
}

// ============================================================
const Splash = ({ msg }) => (
  <div className="min-h-screen grid place-items-center bg-[#0B1220] text-white text-sm">
    <div className="text-center space-y-2">
      <div className="w-10 h-10 mx-auto rounded-full border-2 border-[#2D5A27] border-t-transparent animate-spin"/>
      <div className="text-white/70">{msg}</div>
    </div>
  </div>
);
